/**
 * Global user identity — fires to all observability and analytics providers
 * when a user logs in or their identity changes.
 *
 * This is the single place where user context is propagated to:
 * - Better Stack (error tracking: link errors to users)
 * - Statsig (analytics: feature gates, experiments, funnels)
 * - Canny (feedback: link board posts to users)
 *
 * Call `identifyGlobal()` when the user authenticates or wallet changes.
 * Call `resetIdentity()` on logout.
 */

import { identifyUser as identifyAnalytics } from '@/lib/analytics';
import { env } from '@/lib/config/env';
import { identifyUser as identifyTelemetry } from '@/lib/telemetry';

interface GlobalIdentity {
  /** Internal user ID (from auth provider). */
  userId: string;
  /** User email (for Better Stack user identification). */
  email?: string;
  /** All wallet addresses associated with this user. */
  walletAddresses?: string[];
  /** Currently active wallet address. */
  activeWalletAddress?: string;
  /** Display name (if set). */
  displayName?: string;
  /** X (Twitter) handle. */
  handle?: string;
  /** User's country (for analytics segmentation). */
  country?: string;
  /** ISO-8601 account creation date (for Canny). */
  createdAt?: string;
  /** Avatar URL (for Canny). */
  avatarUrl?: string;
}

/**
 * Identify the current user across all providers.
 * Called from the auth provider when auth state changes to authenticated.
 */
export function identifyGlobal(identity: GlobalIdentity): void {
  // Better Stack — identify user with ALL wallets so we can search by any address.
  // Only truncated addresses are sent (privacy: no full public keys to third parties).
  const telemetryUser: Parameters<typeof identifyTelemetry>[0] = { id: identity.userId };
  if (identity.email) {
    telemetryUser.email = identity.email;
  }
  if (identity.activeWalletAddress) {
    telemetryUser.address = `${identity.activeWalletAddress.slice(0, 8)}...`;
  }
  if (identity.displayName) {
    telemetryUser.username = identity.displayName;
  }
  // Send all wallet addresses as a comma-separated string for searchability
  if (identity.walletAddresses && identity.walletAddresses.length > 0) {
    telemetryUser.all_wallets = identity.walletAddresses
      .map((addr) => `${addr.slice(0, 8)}...`)
      .join(', ');
  }
  identifyTelemetry(telemetryUser);

  // Statsig — email/country are first-class user fields; the rest lands in
  // `custom` (with platform always forced to `web`). Customer.io identify is
  // a separate auth-owned path and ignores this call.
  identifyAnalytics(identity.userId, {
    email: identity.email,
    country: identity.country,
    handle: identity.handle,
    walletAddress: identity.activeWalletAddress,
    walletAddresses: identity.walletAddresses?.join(','),
    walletCount: String(identity.walletAddresses?.length ?? 0),
    displayName: identity.displayName,
  });

  // Canny — link feedback board posts to authenticated users
  identifyCanny(identity);
}

/**
 * Clear user identity on logout.
 * Better Stack and Statsig will treat subsequent events as anonymous.
 */
export function resetIdentity(): void {
  // Statsig — reset to anonymous
  identifyAnalytics('', {});

  // Canny — reset to anonymous
  resetCanny();
}

// ---------------------------------------------------------------------------
// Canny SDK — lazy-loaded on first identify call
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    Canny?: CannyFn;
  }
}

// ALLOW-UNKNOWN: Canny SDK command queue accepts arbitrary argument tuples
type CannyArgs = [string, Record<string, string | Record<string, string>>?];
type CannyFn = {
  (command: string, options?: Record<string, string | Record<string, string>>): void;
  q: CannyArgs[];
};

let cannyScriptLoaded = false;

/** Inject the Canny SDK script tag once. */
function loadCannyScript(): void {
  if (cannyScriptLoaded) return;
  cannyScriptLoaded = true;

  if (typeof window === 'undefined') return;

  // Install the Canny command queue if not already present
  if (typeof window.Canny !== 'function') {
    const c: CannyFn = function cannyQueue(
      command: string,
      options?: Record<string, string | Record<string, string>>,
    ): void {
      if (options) {
        c.q.push([command, options]);
      } else {
        c.q.push([command]);
      }
    } as CannyFn;
    c.q = [];
    window.Canny = c;
  }

  // Inject the SDK script tag
  const existing = document.getElementById('canny-jssdk');
  if (existing) return;
  const first = document.getElementsByTagName('script')[0];
  if (!first?.parentNode) return;
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.id = 'canny-jssdk';
  script.src = 'https://sdk.canny.io/sdk.js';
  first.parentNode.insertBefore(script, first);
}

function identifyCanny(identity: GlobalIdentity): void {
  const appId = env.NEXT_PUBLIC_CANNY_APP_ID;
  if (!appId || typeof window === 'undefined') return;
  // Canny REQUIRES a valid email — identifying without one fails loudly in the
  // console on every auth change (7k warns/48h from X-signup users, who have
  // no email). Skip instead: those users post as anonymous either way.
  if (!identity.email) return;

  loadCannyScript();

  const user: Record<string, string> = {
    id: identity.userId,
    name: identity.displayName ?? identity.email ?? identity.userId,
    email: identity.email,
  };
  if (identity.createdAt) {
    user.created = new Date(identity.createdAt).toISOString();
  }
  if (identity.avatarUrl) {
    user.avatarURL = identity.avatarUrl;
  }

  window.Canny?.('identify', { appID: appId, user });
}

function resetCanny(): void {
  if (typeof window === 'undefined' || typeof window.Canny !== 'function') return;
  // Re-identify without user data resets Canny to anonymous
  const appId = env.NEXT_PUBLIC_CANNY_APP_ID;
  if (!appId) return;
  window.Canny('identify', { appID: appId });
}
